import { ObjectId } from '@freightview/object-id'
import { sum } from 'lodash-es'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { createStore, useStore } from 'zustand'
import { useShallow } from 'zustand/shallow'

import { generateCharge } from '@fv/client-core'
import { getFuelChargeDetail } from '@fv/models'
import { toggleArrayItem } from '@fv/models/core'

import { Loading } from '@/components/shared/Loading'
import { useAppSettings } from '@/hooks/settings'
import type { Opportunity } from '@/types/Opportunity'

import { useQuotes } from '../opp-card/hooks/useQuotes'
import type { Model, QuotesFormState, QuotesFormStore } from './types'
import { createQuoteEditModel, useEquimentTypeOptions } from './utils'

const createDefaultModel = (opportunity: Opportunity): Model => {
  const saltDetails = opportunity.saltDetails

  return {
    amount: saltDetails?.amount ?? 0,
    currency: saltDetails?.currency ?? 'usd',
    equipmentType: opportunity.equipmentRequested.type,
    quoteNum: '',
    quoteId: new ObjectId().toHexString(),
    ...(saltDetails?.charges && {
      charges: saltDetails.charges.map(charge => generateCharge(charge)),
    }),
  }
}

const createQuotesFormStore = (state?: QuotesFormState) => {
  const defaultStore: QuotesFormState = {
    ...state,
  }
  return createStore<QuotesFormStore>()(set => ({
    ...defaultStore,
    actions: {
      setMessage: message => set({ message }),
      resetModels: models => set({ models }),
      toggleSelected: (id, toggle) =>
        set(p => ({ selected: toggleArrayItem(p.selected, id, toggle) })),
      toggleContractedRateLock: () =>
        set(p => ({ contractedRatesLocked: !p.contractedRatesLocked })),
      setContractedRateLock: contractedRatesLocked =>
        set({ contractedRatesLocked }),
      updateModel: updatedModel =>
        set(prev => ({
          models: prev.models.map(m =>
            m.quoteId === updatedModel.quoteId ? updatedModel : m,
          ),
        })),
    },
  }))
}

type QuotesFormStoreEntity = ReturnType<typeof createQuotesFormStore>
const QuotesFormContext = createContext<QuotesFormStoreEntity | null>(null)

type Props = PropsWithChildren<{
  opportunity: Opportunity
}>
export const QuotesFormProvider = ({ children, opportunity }: Props) => {
  const equipmentOptions = useEquimentTypeOptions(opportunity)
  const quotesQuery = useQuotes(opportunity.loadId)
  const settingsQuery = useAppSettings()
  const defaultModel = createDefaultModel(opportunity)
  const store = useRef(
    createQuotesFormStore({
      opportunity,
      message: '',
      models: [defaultModel],
      selected: opportunity.saltDetails ? [defaultModel.quoteId] : [],
      equipmentOptions,
      contractedRatesLocked: true,
    }),
  ).current
  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  useEffect(() => {
    if (quotesQuery.data && quotesQuery.data.length) {
      store.setState({
        models: quotesQuery.data.map(createQuoteEditModel),
        selected: quotesQuery.data.map(q => q._id),
      })
    }
  }, [quotesQuery.data])

  if (quotesQuery.isLoading || settingsQuery.isLoading) {
    return <Loading />
  }

  return (
    <QuotesFormContext.Provider value={store}>
      {children}
    </QuotesFormContext.Provider>
  )
}

export function useQuotesFormContext<T>(
  selector: (state: QuotesFormStore) => T,
): T {
  const store = useContext(QuotesFormContext)
  if (!store) throw new Error('Missing QuotesFormProvider in the tree')
  return useStore(store, selector)
}

export const useQuotesFormState = () => {
  return useQuotesFormContext(useShallow(({ actions, ...state }) => state))
}

export const useQuotesFormActions = () => {
  return useQuotesFormContext(({ actions }) => actions)
}

export const useQuotesFormHelpers = () => {
  const { updateModel } = useQuotesFormActions()
  const { opportunity } = useQuotesFormState()

  const switchToCharges = (model: Model) => {
    const charges = [generateCharge({ name: 'linehaul', amount: model.amount })]

    // If breakthrough fuel is enabled, automatically add a fuel charge with the cost
    if (
      opportunity?.fuel?.provider === 'breakthrough' &&
      opportunity?.fuel?.cost
    ) {
      charges.push(
        generateCharge({
          name: 'fuel surcharge',
          amount: opportunity.fuel.cost,
          providerDetail: getFuelChargeDetail(opportunity.fuel.provider),
        }),
      )
    } else {
      charges.push({ ...generateCharge() })
    }

    updateModel({
      ...model,
      charges,
    })
  }

  const switchFromCharges = (model: Model) =>
    updateModel({
      ...model,
      amount: sum(model.charges?.map(c => c.amount)),
      charges: undefined,
    })

  return {
    switchToCharges,
    switchFromCharges,
  }
}
